/**
 * TaskDetailModal Component
 *
 * Modal di dettaglio/modifica di un task Kanban Planner: header con stato e azione
 * completa/riapri, tab Dettagli (sempre editabile), Checklist (ChecklistManager invariato),
 * Allegati (TaskAttachmentsPanel), più i placeholder invariati Commenti/Cronologia
 * (fuori scope per questa iterazione).
 */
import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import {
  Alert,
  Badge,
  Button,
  Group,
  Image,
  Loader,
  Modal,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconArrowBackUp,
  IconCheck,
  IconCheckbox,
  IconEdit,
  IconHistory,
  IconMessage,
  IconPaperclip,
} from '@tabler/icons-react';

import {
  useCompleteKanbanTask,
  useReopenKanbanTask,
  useTaskDetail,
  useUpdateKanbanTask,
} from '../../hooks/use-kanban-tasks';
import { useKanbanColumns } from '../../hooks/use-kanban-columns';
import { getTaskStatusColor, getTaskStatusLabel } from '../../utils/task-status-utils';
import { diffInDays, parseDateOnly, toDateOnlyString } from '../../utils/date-utils';
import UserMultiSelect from '../kanban/UserMultiSelect';
import TaskAttachmentsPanel from '../kanban/attachments/TaskAttachmentsPanel';
import ChecklistManager from '../checklist/ChecklistManager';

interface TaskDetailModalProps {
  taskId: number;
  opened: boolean;
  onClose: () => void;
  onTaskUpdated?: () => void;
}

interface DetailFormValues {
  title: string;
  description: string;
  priorityString: string;
  assigneeIds: number[];
  kanbanColumnId: string;
  dueDate: Date | null;
  completedAt: Date | null;
}

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Bassa' },
  { value: 'Medium', label: 'Media' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Critica' },
];

export default function TaskDetailModal({ taskId, opened, onClose, onTaskUpdated }: TaskDetailModalProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [completePopoverOpen, setCompletePopoverOpen] = useState(false);
  const [completeDate, setCompleteDate] = useState<Date | null>(new Date());

  const { data: task, isLoading } = useTaskDetail(taskId);
  const { data: columns = [] } = useKanbanColumns();
  const updateTask = useUpdateKanbanTask();
  const completeTask = useCompleteKanbanTask();
  const reopenTask = useReopenKanbanTask();

  const form = useForm<DetailFormValues>({
    initialValues: {
      title: '',
      description: '',
      priorityString: 'Medium',
      assigneeIds: [],
      kanbanColumnId: '',
      dueDate: null,
      completedAt: null,
    },
  });

  useEffect(() => {
    if (task) {
      form.setValues({
        title: task.title,
        description: task.description || '',
        priorityString: task.priorityString,
        assigneeIds: task.assignees?.map((a) => a.userId) ?? [],
        kanbanColumnId: task.kanbanColumnId != null ? String(task.kanbanColumnId) : '',
        dueDate: parseDateOnly(task.dueDate),
        completedAt: parseDateOnly(task.completedAt),
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  useEffect(() => {
    if (!opened) {
      setActiveTab('details');
      setCompletePopoverOpen(false);
      setCompleteDate(new Date());
    }
  }, [opened]);

  const handleSave = form.onSubmit((values) => {
    updateTask.mutate(
      {
        id: taskId,
        dto: {
          title: values.title.trim(),
          description: values.description?.trim() || undefined,
          priorityString: values.priorityString,
          assigneeIds: values.assigneeIds,
          kanbanColumnId: values.kanbanColumnId ? Number(values.kanbanColumnId) : undefined,
          dueDate: values.dueDate ? toDateOnlyString(values.dueDate) : undefined,
          completedAt: task?.isCompleted ? (values.completedAt ? toDateOnlyString(values.completedAt) : null) : undefined,
        },
      },
      {
        onSuccess: () => {
          onTaskUpdated?.();
        },
      }
    );
  });

  const handleComplete = () => {
    completeTask.mutate(
      { id: taskId, completedAt: completeDate ? toDateOnlyString(completeDate) : undefined },
      {
        onSuccess: () => {
          setCompletePopoverOpen(false);
          onTaskUpdated?.();
        },
      }
    );
  };

  const handleReopen = () => {
    reopenTask.mutate(taskId, { onSuccess: () => onTaskUpdated?.() });
  };

  const columnOptions = columns.filter((c) => c.isActive).map((c) => ({ value: String(c.id), label: c.name }));
  const completedDelta = task?.isCompleted ? diffInDays(form.values.completedAt, form.values.dueDate) : null;

  if (isLoading) {
    return (
      <Modal opened={opened} onClose={onClose} title="Caricamento..." size="xl" centered>
        <Stack align="center" py="xl">
          <Loader size="lg" />
          <Text>Caricamento dettagli task...</Text>
        </Stack>
      </Modal>
    );
  }

  if (!task) {
    return (
      <Modal opened={opened} onClose={onClose} title="Errore" size="md" centered>
        <Stack align="center" py="xl">
          <Alert color="red" icon={<IconAlertCircle />}>
            Task non trovato o impossibile da caricare
          </Alert>
          <Button onClick={onClose}>Chiudi</Button>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={600}>Task #{taskId}</Text>
          <Badge variant="filled" color={getTaskStatusColor(task)} style={{ color: 'white' }}>
            {getTaskStatusLabel(task)}
          </Badge>
          {task.requestLabel && (
            <Text size="sm" c="dimmed">
              {task.requestLabel}
            </Text>
          )}
        </Group>
      }
      size="xl"
      styles={{ body: { padding: 0 }, header: { borderBottom: '1px solid #e9ecef' } }}
    >
      {task.coverUrl && <Image src={task.coverUrl} h={100} fit="cover" />}

      <Group justify="flex-end" px="md" pt="sm">
        {task.isCompleted ? (
          <Button variant="light" leftSection={<IconArrowBackUp size={16} />} onClick={handleReopen} loading={reopenTask.isPending}>
            Riapri
          </Button>
        ) : (
          <Popover opened={completePopoverOpen} onChange={setCompletePopoverOpen} withArrow position="bottom-end">
            <Popover.Target>
              <Button
                variant="light"
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={() => setCompletePopoverOpen((o) => !o)}
              >
                Segna come completato
              </Button>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="sm">
                <DatePickerInput label="Data effettiva" locale="it" valueFormat="DD/MM/YYYY" value={completeDate} onChange={setCompleteDate} />
                <Button size="xs" onClick={handleComplete} loading={completeTask.isPending}>
                  Conferma
                </Button>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        )}
      </Group>

      <Tabs value={activeTab} onChange={(v) => setActiveTab(v || 'details')}>
        <Tabs.List px="md" pt="sm">
          <Tabs.Tab value="details" leftSection={<IconEdit size={16} />}>
            Dettagli
          </Tabs.Tab>
          <Tabs.Tab value="checklist" leftSection={<IconCheckbox size={16} />}>
            Checklist
          </Tabs.Tab>
          <Tabs.Tab
            value="attachments"
            leftSection={<IconPaperclip size={16} />}
            rightSection={
              task.attachmentsCount > 0 ? (
                <Badge size="xs" variant="filled" color="blue" style={{ color: 'white' }}>
                  {task.attachmentsCount}
                </Badge>
              ) : undefined
            }
          >
            Allegati
          </Tabs.Tab>
          <Tabs.Tab value="comments" leftSection={<IconMessage size={16} />}>
            Commenti
          </Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
            Cronologia
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details">
          <ScrollArea h="60vh" px="md" py="sm">
            <form onSubmit={handleSave}>
              <Stack gap="md">
                <TextInput label="Titolo" required {...form.getInputProps('title')} />
                <Textarea label="Descrizione" rows={4} {...form.getInputProps('description')} />

                <Group grow>
                  <Select label="Priorità" data={PRIORITY_OPTIONS} {...form.getInputProps('priorityString')} />
                  <Select label="Colonna" data={columnOptions} {...form.getInputProps('kanbanColumnId')} />
                </Group>

                <UserMultiSelect
                  value={form.values.assigneeIds}
                  onChange={(ids) => form.setFieldValue('assigneeIds', ids)}
                  label="Assegnatari"
                />

                <Group grow align="flex-start">
                  <DatePickerInput label="Data prevista" locale="it" valueFormat="DD/MM/YYYY" clearable {...form.getInputProps('dueDate')} />
                  {task.isCompleted && (
                    <Stack gap={4}>
                      <DatePickerInput label="Data effettiva" locale="it" valueFormat="DD/MM/YYYY" {...form.getInputProps('completedAt')} />
                      {completedDelta != null && (
                        <Badge size="sm" variant="light" color={completedDelta <= 0 ? 'green' : 'red'} w="fit-content">
                          {completedDelta <= 0 ? 'In tempo' : `+${completedDelta} gg`}
                        </Badge>
                      )}
                    </Stack>
                  )}
                </Group>

                <Group justify="flex-end">
                  <Button type="submit" loading={updateTask.isPending} disabled={!form.isDirty()}>
                    Salva
                  </Button>
                </Group>
              </Stack>
            </form>
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="checklist">
          <div style={{ height: '60vh', padding: '0 1rem' }}>
            <ChecklistManager taskId={taskId} />
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="attachments">
          <ScrollArea h="60vh" px="md" py="sm">
            <TaskAttachmentsPanel taskId={taskId} />
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="comments">
          <ScrollArea h="60vh" px="md" py="sm">
            <Stack>
              <Text c="dimmed">Funzionalità commenti in sviluppo</Text>
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="history">
          <ScrollArea h="60vh" px="md" py="sm">
            <Stack>
              <Text c="dimmed">Cronologia modifiche in sviluppo</Text>
            </Stack>
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
